import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import type { User, Session } from "@/modules/shared/hooks/useAuth";
import { db } from "@/modules/shared/lib/db-client";
import { setObservabilityUser, clearObservabilityUser, log, pushEvent } from "@/modules/shared/lib/observability";

interface ClientInfo {
  id: string;
  name: string;
  logo_url: string | null;
  sector: string | null;
}

interface PortalAuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  clientId: string | null;
  clientInfo: ClientInfo | null;
  isPortalUser: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, inviteToken: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const PortalAuthContext = createContext<PortalAuthContextType | undefined>(undefined);

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [clientId, setClientId] = useState<string | null>(null);
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null);
  const [isPortalUser, setIsPortalUser] = useState(false);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = db.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Defer the client lookup
        if (session?.user) {
          setObservabilityUser({ id: session.user.id, email: session.user.email });
          pushEvent("flow.portal_login.success");
          setTimeout(() => {
            fetchClientAccess(session.user.id);
          }, 0);
        } else {
          clearObservabilityUser();
          setClientId(null);
          setClientInfo(null);
          setIsPortalUser(false);
          setLoading(false);
        }
      }
    );

    // THEN check for existing session
    db.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchClientAccess(session.user.id);
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchClientAccess = async (userId: string) => {
    try {
      // Check if user is a client manager (contact with portal access)
      const { data: contactData, error } = await db
        .from("client_contacts")
        .select("client_id, invite_status, has_portal_access")
        .eq("user_id", userId)
        .eq("has_portal_access", true)
        .eq("invite_status", "accepted")
        .maybeSingle();

      if (error) {
        log.error("Error fetching client access", {}, error);
        setLoading(false);
        return;
      }

      if (contactData) {
        setClientId(contactData.client_id);
        setIsPortalUser(true);

        // Fetch client info
        const { data: client } = await db
          .from("clients")
          .select("id, name, logo_url, sector")
          .eq("id", contactData.client_id)
          .single();

        if (client) {
          setClientInfo(client);
        }
      } else {
        setIsPortalUser(false);
        setClientId(null);
        setClientInfo(null);
      }
    } catch (err) {
      log.error("Error in fetchClientAccess", {}, err);
    }
    setLoading(false);
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await db.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, inviteToken: string) => {
    // First, verify the invite token in client_contacts
    const { data: invite, error: inviteError } = await db
      .from("client_contacts")
      .select("id, email, client_id")
      .eq("invite_token", inviteToken)
      .eq("has_portal_access", true)
      .eq("invite_status", "pending")
      .maybeSingle();

    if (inviteError || !invite) {
      return { error: new Error("Convite inválido ou expirado") };
    }

    // Check email matches
    if (invite.email.toLowerCase() !== email.toLowerCase()) {
      return { error: new Error("Email não corresponde ao convite") };
    }

    // Create account
    const redirectUrl = `${window.location.origin}/portal`;
    const { data: authData, error: authError } = await db.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: email.split("@")[0], // Will be updated from invite
        },
      },
    });

    if (authError) {
      return { error: authError as Error };
    }

    // Link user to client_contacts record
    if (authData.user) {
      const { error: updateError } = await db
        .from("client_contacts")
        .update({
          user_id: authData.user.id,
          invite_status: "accepted",
        })
        .eq("id", invite.id);

      if (updateError) {
        log.error("Error updating invite", {}, updateError);
      }

      // Add client_manager role
      await db.from("user_roles").insert({
        user_id: authData.user.id,
        role: "client_manager",
      } as any);
    }

    return { error: null };
  };

  const signOut = async () => {
    clearObservabilityUser();
    await db.auth.signOut();
    setClientId(null);
    setClientInfo(null);
    setIsPortalUser(false);
  };

  return (
    <PortalAuthContext.Provider
      value={{
        user,
        session,
        loading,
        clientId,
        clientInfo,
        isPortalUser,
        signIn,
        signUp,
        signOut,
      }}
    >
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth() {
  const context = useContext(PortalAuthContext);
  if (context === undefined) {
    throw new Error("usePortalAuth must be used within a PortalAuthProvider");
  }
  return context;
}
